import { useCallback, useEffect, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';

import verificationIcon from '../../constants/images/modal-verification.svg';
import upload from '../../constants/images/upload-icon.svg';
import trashIcon from '../../constants/images/trash-icon.svg';
import { verificationService } from '../../services/verification.service';
import { compressDocument } from '../../utils/imageCompression';
import styles from './ModalReVerification.module.scss';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];
const MAX_SIZE = 20 * 1024 * 1024; // 20 MB

// Human labels mirroring the backend VerificationDocumentTypeChoices /
// VerificationReasonCode enums.
const DOC_TYPE_LABELS = {
  // One identity document, two accepted formats (booklet passport OR ID-card).
  passport: 'Паспорт / ID-картка',
  id_card: 'Паспорт / ID-картка',
  selfie: 'Селфі',
  taxpayer: 'ІПН',
  other: 'Документ',
};

const REASON_CODE_LABELS = {
  unreadable_docs: 'Документи нечитабельні або низької якості',
  data_mismatch: 'Дані в документах не збігаються',
  selfie_mismatch: 'Селфі не відповідає документу',
  missing_docs: 'Бракує документів',
  fraud_suspected: 'Підозра на шахрайство',
  other: 'Інша причина',
};

const validateFile = (file, setError) => {
  if (!ALLOWED_TYPES.includes(file.type)) {
    setError('Дозволені тільки файли типу PNG, JPG, JPEG');
    return false;
  }
  if (file.size > MAX_SIZE) {
    setError('Максимальний розмір файлу - 20 МБ');
    return false;
  }
  return true;
};

// Re-verification prompt shown to a cleaner whose documents need attention
// (needs_reverification === true). Two modes driven by the verification attempt:
//   - PARTIAL (is_changes_requested): the moderator rejected specific documents;
//     the cleaner re-uploads ONLY those, one at a time. Once none remain
//     rejected the attempt returns to "under review".
//   - FULL (rejected / deactivated / no documents yet): submit a brand-new
//     attempt with passport + selfie (+ optional taxpayer).
const ModalReVerification = ({ reason, verification: initialVerification = null }) => {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [verification, setVerification] = useState(initialVerification);
  // Full-mode files.
  const [passport, setPassport] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [taxpayer, setTaxpayer] = useState(null);
  // Partial-mode: the originally-requested doc types (stable — the server drops
  // a doc from the rejected set once re-uploaded, but we keep its tile) and the
  // per-type re-upload preview so the cleaner SEES what they submitted instead
  // of the tile just vanishing.
  const [requested, setRequested] = useState([]);
  const [reuploaded, setReuploaded] = useState({});
  const [uploadingType, setUploadingType] = useState(null);

  useEffect(() => {
    setVerification(initialVerification);
  }, [initialVerification]);

  const isPartial = Boolean(verification?.is_changes_requested);

  // Capture the requested doc types once, on entering partial mode.
  useEffect(() => {
    if (isPartial && requested.length === 0) {
      const rejected = (verification?.documents || []).filter((d) => d.status === 'rejected');
      if (rejected.length) {
        setRequested(rejected.map((d) => ({ type: d.type, reason: d.reason })));
      }
    }
  }, [isPartial, verification, requested.length]);

  useEffect(() => {
    if (error) {
      toast.error(typeof error === 'string' ? error : JSON.stringify(error), {
        duration: 5000,
        id: 'reverification-error',
      });
      setError('');
    }
  }, [error]);

  // ----- FULL mode (new attempt) -----
  const handleSelectFile = useCallback(async (e, setter) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!validateFile(file, setError)) return;
    // Compress identity documents gently (near-lossless) before upload.
    setter(await compressDocument(file));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!passport || !selfie) {
      setError('Завантажте, будь ласка, фото паспорта / ID-картки та селфі');
      return;
    }
    setIsLoading(true);
    const formData = new FormData();
    formData.append('passport', passport);
    formData.append('selfie', selfie);
    if (taxpayer) {
      formData.append('taxpayer', taxpayer);
    }
    try {
      await verificationService.submit(formData);
      setIsSubmitted(true);
    } catch (err) {
      setError(err?.response?.data || 'Не вдалося надіслати документи. Спробуйте ще раз.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [passport, selfie, taxpayer]);

  // ----- PARTIAL mode (re-upload one requested document) -----
  const handleReupload = useCallback(async (e, docType) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!validateFile(file, setError)) return;

    setIsLoading(true);
    setUploadingType(docType);
    try {
      const compressed = await compressDocument(file);
      // Keep a local preview of what was just submitted — the tile stays
      // visible with this thumbnail (+ "На перевірці") instead of disappearing.
      const preview = URL.createObjectURL(compressed);
      const formData = new FormData();
      formData.append('type', docType);
      formData.append('file', compressed);
      await verificationService.uploadDocument(formData);
      setReuploaded((prev) => ({ ...prev, [docType]: { preview } }));

      // Re-read status so the backend-derived state stays fresh (it supersedes
      // the old rejected doc). We keep our local preview regardless.
      try {
        const { data } = await verificationService.getStatus();
        setVerification(data?.verification || null);
      } catch (statusErr) {
        console.error(statusErr);
      }
    } catch (err) {
      setError(err?.response?.data || 'Не вдалося завантажити документ. Спробуйте ще раз.');
      console.error(err);
    } finally {
      setIsLoading(false);
      setUploadingType(null);
    }
  }, []);

  if (isSubmitted) {
    return (
      <div className={styles.modal}>
        <div className={styles.modal_content}>
          <img src={verificationIcon} alt="verification icon" />
          <h2>ДОКУМЕНТИ НА ПЕРЕВІРЦІ</h2>
          <p>
            Дякуємо! Ваші документи надіслано на перевірку. Протягом 3 днів з Вами
            звʼяжеться наш спеціаліст для завершення верифікації.
          </p>
        </div>
        <Toaster />
      </div>
    );
  }

  // ===== PARTIAL: re-upload only the requested documents =====
  if (isPartial) {
    // Show every originally-requested type. Fall back to the live rejected set
    // for the very first render before `requested` is captured.
    const items = requested.length
      ? requested
      : (verification?.documents || [])
          .filter((d) => d.status === 'rejected')
          .map((d) => ({ type: d.type, reason: d.reason }));
    const allDone = items.length > 0 && items.every((d) => reuploaded[d.type]);

    return (
      <div className={styles.modal}>
        <div className={styles.modal_content}>
          <img src={verificationIcon} alt="verification icon" />
          <h2>{allDone ? 'ДОКУМЕНТИ НА ПЕРЕВІРЦІ' : 'ОЧІКУЄ ВИПРАВЛЕНЬ'}</h2>
          <p>
            {allDone
              ? 'Дякуємо! Оновлені документи надіслано на перевірку. Протягом 3 днів з Вами звʼяжеться наш спеціаліст для завершення верифікації.'
              : 'Деякі документи потребують повторного завантаження. Завантажте заново лише позначені нижче — решта вже на перевірці.'}
          </p>

          <div className={styles.modal_fields}>
            {items.map((doc) => {
              const inputId = `reupload-${doc.type}`;
              const uploaded = reuploaded[doc.type];
              const label = DOC_TYPE_LABELS[doc.type] || 'Документ';
              return (
                <div className={styles.modal_field} key={doc.type}>
                  <p className={styles.modal_field_label}>
                    {uploaded ? label : `${label} — відхилено`}
                  </p>
                  {!uploaded && doc.reason ? (
                    <p style={{ color: '#d14343', fontSize: '0.85rem', marginBottom: 8 }}>
                      Причина: {doc.reason}
                    </p>
                  ) : null}
                  <input
                    type="file"
                    id={inputId}
                    style={{ display: 'none' }}
                    onChange={(e) => handleReupload(e, doc.type)}
                    autoComplete="off"
                    disabled={isLoading}
                  />
                  {uploaded ? (
                    // Preview of the just-submitted document. Tap to replace it.
                    <div
                      className={styles.modal_field_preview}
                      onClick={() => !isLoading && document.getElementById(inputId).click()}
                      style={{ backgroundImage: `url(${uploaded.preview})` }}
                    >
                      <div className={styles.modal_field_preview_badge}>На перевірці</div>
                    </div>
                  ) : (
                    <div
                      onClick={() => !isLoading && document.getElementById(inputId).click()}
                      className={styles.addButton}
                    >
                      <img src={upload} alt="upload icon" />
                      <p>{uploadingType === doc.type ? 'Завантаження…' : 'Завантажити заново (PNG, JPG, JPEG)'}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <Toaster />
      </div>
    );
  }

  // ===== FULL: submit a new attempt =====
  const renderUpload = (label, file, inputId, setter) => (
    <div className={styles.modal_field}>
      <p className={styles.modal_field_label}>{label}</p>
      <input
        type="file"
        id={inputId}
        style={{ display: 'none' }}
        onChange={(e) => handleSelectFile(e, setter)}
        autoComplete="off"
      />
      {file ? (
        <div
          className={styles.modal_field_preview}
          onClick={() => document.getElementById(inputId).click()}
          style={{ backgroundImage: `url(${URL.createObjectURL(file)})` }}
        >
          <div
            className={styles.modal_field_preview_remove}
            onClick={(event) => {
              event.stopPropagation();
              setter(null);
            }}
          >
            <img src={trashIcon} alt="trash-icon" />
          </div>
        </div>
      ) : (
        <div onClick={() => document.getElementById(inputId).click()} className={styles.addButton}>
          <img src={upload} alt="upload icon" />
          <p>Формат: PNG, JPG, JPEG (Макс. 20мб)</p>
        </div>
      )}
    </div>
  );

  const reasonCode = verification?.reason_code;
  const reasonText = reason || verification?.reason;

  return (
    <div className={styles.modal}>
      <div className={styles.modal_content}>
        <img src={verificationIcon} alt="verification icon" />
        <h2>ПОТРІБНА ПОВТОРНА ВЕРИФІКАЦІЯ</h2>
        <p>
          Ваші документи потребують повторної перевірки. Завантажте, будь ласка, фото паспорта /
          ID-картки та селфі ще раз.
          {reasonCode ? ` Причина: ${REASON_CODE_LABELS[reasonCode] || reasonCode}.` : ''}
          {reasonText ? ` ${reasonText}` : ''}
        </p>

        <div className={styles.modal_fields}>
          {renderUpload('Фото паспорта / ID-картки', passport, 'reverificationPassport', setPassport)}
          {renderUpload('Селфі', selfie, 'reverificationSelfie', setSelfie)}
          {renderUpload('ІПН (за бажанням)', taxpayer, 'reverificationTaxpayer', setTaxpayer)}
        </div>

        <button
          disabled={!passport || !selfie || isLoading}
          onClick={handleSubmit}
          className={styles.modal_submit}
        >
          {isLoading ? 'Надсилання...' : 'Надіслати на перевірку'}
        </button>
      </div>
      <Toaster />
    </div>
  );
};

export default ModalReVerification;
